/**
 * Leave Management Hooks
 *
 * React Query hooks for the HRMS Leave Management module.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { delegationKeys } from '@/hooks/useDelegations';
import { queryKeys } from '@/lib/query-config';
import { parseApiError, getErrorMessage } from '@/lib/api-errors';
import {
  fetchLeaveTypes,
  fetchLeaveBalance,
  fetchLeaveRequests,
  fetchLeaveRequest,
  createLeaveRequest,
  calculateLeaveRequestDays,
  updateLeaveRequest,
  submitLeaveRequest,
  approveLeaveRequest,
  rejectLeaveRequest,
  cancelLeaveRequest,
  withdrawLeaveRequest,
  createLeaveType,
  updateLeaveType,
  deleteLeaveType,
  initializeLeaveTypes,
  initializeAllBalances,
  fetchLeaveAdminOperationPreview,
  fetchLeaveAdminOperationPreviewDetails,
  fetchLeaveAdminOperationRuns,
  fetchLeaveAttachments,
  uploadLeaveAttachment,
  deleteLeaveAttachment,
  fetchApprovalStatus,
  fetchLeaveTransactions,
  createEncashment,
  fetchEncashments,
  approveEncashment,
  rejectEncashment,
  adjustLeaveBalance,
  createDelegation,
  cancelDelegation,
  triggerMonthlyAccrual,
  triggerQuarterlyAccrual,
  triggerYearEndRollover,
  triggerExpiryCheck,
  fetchManagedEmployees,
} from '../services/leave.service';
import type {
  LeaveRequest,
  LeaveQueryFilters,
  TeamView,
  UpdateLeaveRequestInput,
  CalculateLeaveDaysInput,
  ApproveLeaveInput,
  RejectLeaveInput,
  CancelLeaveInput,
  WithdrawLeaveInput,
  UpdateLeaveTypeInput,
  LeaveTransactionFilters,
  LeaveAdminOperationType,
} from '../types';

function requireQueryString(value: string | null | undefined, label: string): string {
  if (!value) {
    throw new Error(`${label} is required for this query`);
  }
  return value;
}

function requireQueryInput<TInput>(value: TInput | null | undefined, label: string): TInput {
  if (!value) {
    throw new Error(`${label} is required for this query`);
  }
  return value;
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch all leave types
 */
export function useLeaveTypes(options: { includeInactive?: boolean } = {}) {
  return useQuery({
    queryKey: ['leaveTypes', options.includeInactive ? 'includeInactive' : 'active'],
    queryFn: () => fetchLeaveTypes(options),
    staleTime: 30 * 60 * 1000, // 30 minutes - leave types rarely change
  });
}

/**
 * Fetch leave balance for an employee
 */
export function useLeaveBalance(employeeId: string | null, year?: number) {
  return useQuery({
    queryKey: queryKeys.leaves.balance(employeeId ?? ''),
    queryFn: () => fetchLeaveBalance(requireQueryString(employeeId, 'Employee ID'), year),
    enabled: !!employeeId,
  });
}

/**
 * Fetch leave requests with filters
 */
export function useLeaveRequests(filters?: LeaveQueryFilters) {
  return useQuery({
    queryKey: queryKeys.leaves.list({ ...(filters ?? {}) }),
    queryFn: () => fetchLeaveRequests(filters),
  });
}

/**
 * Fetch leave requests with infinite scroll
 */
export function useInfiniteLeaveRequests(filters?: Omit<LeaveQueryFilters, 'page'>) {
  return useInfiniteQuery({
    queryKey: queryKeys.leaves.list({ ...filters, infinite: true }),
    queryFn: ({ pageParam = 1 }) =>
      fetchLeaveRequests({ ...filters, page: pageParam, limit: filters?.limit ?? 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return allPages.length + 1;
      }
      return undefined;
    },
  });
}

/**
 * Fetch a single leave request by ID
 */
export function useLeaveRequest(id: string | null) {
  return useQuery({
    queryKey: queryKeys.leaves.detail(id ?? ''),
    queryFn: () => fetchLeaveRequest(requireQueryString(id, 'Leave request ID')),
    enabled: !!id,
  });
}

/**
 * Calculate leave days through the backend policy engine.
 */
export function useLeaveDaysCalculation(input: CalculateLeaveDaysInput | null) {
  return useQuery({
    queryKey: ['leaveDaysCalculation', input],
    queryFn: () => calculateLeaveRequestDays(requireQueryInput(input, 'Leave calculation input')),
    enabled: !!input?.employeeId && !!input?.startDate && !!input?.endDate,
    staleTime: 30 * 1000,
  });
}

export function useLeaveAdminOperationPreview(
  input: {
    operationType: LeaveAdminOperationType;
    targetYear?: number;
    targetMonth?: number;
  } | null,
) {
  return useQuery({
    queryKey: ['leaveAdminOperationPreview', input],
    queryFn: () =>
      fetchLeaveAdminOperationPreview(requireQueryInput(input, 'Leave operation preview input')),
    enabled: !!input,
    staleTime: 15 * 1000,
  });
}

export function useLeaveAdminOperationPreviewDetails(
  input: {
    operationType: LeaveAdminOperationType;
    targetYear?: number;
    targetMonth?: number;
    limit?: number;
  } | null,
) {
  return useQuery({
    queryKey: ['leaveAdminOperationPreviewDetails', input],
    queryFn: () =>
      fetchLeaveAdminOperationPreviewDetails(
        requireQueryInput(input, 'Leave operation preview details input'),
      ),
    enabled: !!input,
    staleTime: 15 * 1000,
  });
}

export function useLeaveAdminOperationRuns(limit = 10) {
  return useQuery({
    queryKey: ['leaveAdminOperationRuns', limit],
    queryFn: () => fetchLeaveAdminOperationRuns(limit),
    refetchInterval: 15 * 1000,
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Create a new leave request
 */
export function useCreateLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLeaveRequest,

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.leaves.all });
      toast.loading('Creating leave request...', { id: 'create-leave' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'create-leave' });
    },

    onSuccess: (data) => {
      toast.success('Leave request created', {
        id: 'create-leave',
        description:
          data.status === 'draft' ? 'Remember to submit for approval' : 'Submitted for approval',
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

/**
 * Update a draft leave request
 */
export function useUpdateLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & UpdateLeaveRequestInput) =>
      updateLeaveRequest(id, input),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.leaves.all });
      toast.loading('Updating leave request...', { id: 'update-leave' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'update-leave' });
    },

    onSuccess: () => {
      toast.success('Leave request updated', { id: 'update-leave' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

/**
 * Submit a draft leave request for approval
 */
export function useSubmitLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submitLeaveRequest,

    onMutate: async (id) => {
      // Cancel ongoing queries (don't await to ensure optimistic update happens quickly)
      queryClient.cancelQueries({ queryKey: queryKeys.leaves.all });

      // Optimistically update the status
      const previousData = queryClient.getQueryData<LeaveRequest>(queryKeys.leaves.detail(id));

      if (previousData) {
        queryClient.setQueryData<LeaveRequest>(queryKeys.leaves.detail(id), {
          ...previousData,
          status: 'submitted',
        });
      }

      toast.loading('Submitting request...', { id: 'submit-leave' });

      return { previousData };
    },

    onError: (error, id, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.leaves.detail(id), context.previousData);
      }
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'submit-leave' });
    },

    onSuccess: () => {
      toast.success('Leave request submitted for approval', { id: 'submit-leave' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

/**
 * Approve a leave request
 */
export function useApproveLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & ApproveLeaveInput) =>
      approveLeaveRequest(id, input),

    onMutate: async ({ id }) => {
      // Cancel ongoing queries (don't await to ensure optimistic update happens quickly)
      queryClient.cancelQueries({ queryKey: queryKeys.leaves.all });

      const previousData = queryClient.getQueryData<LeaveRequest>(queryKeys.leaves.detail(id));

      if (previousData) {
        queryClient.setQueryData<LeaveRequest>(queryKeys.leaves.detail(id), {
          ...previousData,
          status: 'approved',
          approvalDate: new Date().toISOString(),
        });
      }

      toast.loading('Approving request...', { id: 'approve-leave' });

      return { previousData };
    },

    onError: (error, { id }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.leaves.detail(id), context.previousData);
      }
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'approve-leave' });
    },

    onSuccess: () => {
      toast.success('Leave request approved', { id: 'approve-leave' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

/**
 * Reject a leave request
 */
export function useRejectLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & RejectLeaveInput) =>
      rejectLeaveRequest(id, input),

    onMutate: async ({ id }) => {
      // Cancel ongoing queries (don't await to ensure optimistic update happens quickly)
      queryClient.cancelQueries({ queryKey: queryKeys.leaves.all });

      const previousData = queryClient.getQueryData<LeaveRequest>(queryKeys.leaves.detail(id));

      if (previousData) {
        queryClient.setQueryData<LeaveRequest>(queryKeys.leaves.detail(id), {
          ...previousData,
          status: 'rejected',
        });
      }

      toast.loading('Rejecting request...', { id: 'reject-leave' });

      return { previousData };
    },

    onError: (error, { id }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.leaves.detail(id), context.previousData);
      }
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'reject-leave' });
    },

    onSuccess: () => {
      toast.success('Leave request rejected', { id: 'reject-leave' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

/**
 * Cancel a leave request (with reason)
 */
export function useCancelLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & CancelLeaveInput) =>
      cancelLeaveRequest(id, input),

    onMutate: async ({ id }) => {
      queryClient.cancelQueries({ queryKey: queryKeys.leaves.all });

      const previousData = queryClient.getQueryData<LeaveRequest>(queryKeys.leaves.detail(id));

      if (previousData) {
        queryClient.setQueryData<LeaveRequest>(queryKeys.leaves.detail(id), {
          ...previousData,
          status: 'cancelled',
        });
      }

      toast.loading('Cancelling request...', { id: 'cancel-leave' });

      return { previousData };
    },

    onError: (error, { id }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.leaves.detail(id), context.previousData);
      }
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'cancel-leave' });
    },

    onSuccess: () => {
      toast.success('Leave request cancelled', { id: 'cancel-leave' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

/**
 * Withdraw a submitted leave request
 */
export function useWithdrawLeaveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & WithdrawLeaveInput) =>
      withdrawLeaveRequest(id, Object.keys(input).length > 0 ? input : undefined),

    onMutate: async ({ id }) => {
      queryClient.cancelQueries({ queryKey: queryKeys.leaves.all });

      const previousData = queryClient.getQueryData<LeaveRequest>(queryKeys.leaves.detail(id));

      if (previousData) {
        queryClient.setQueryData<LeaveRequest>(queryKeys.leaves.detail(id), {
          ...previousData,
          status: 'withdrawn',
        });
      }

      toast.loading('Withdrawing request...', { id: 'withdraw-leave' });

      return { previousData };
    },

    onError: (error, { id }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.leaves.detail(id), context.previousData);
      }
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'withdraw-leave' });
    },

    onSuccess: () => {
      toast.success('Leave request withdrawn', { id: 'withdraw-leave' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

// =============================================================================
// LEAVE TYPE MANAGEMENT HOOKS
// =============================================================================

/**
 * Create a new leave type
 */
export function useCreateLeaveType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLeaveType,

    onMutate: () => {
      toast.loading('Creating leave type...', { id: 'create-leave-type' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'create-leave-type' });
    },

    onSuccess: () => {
      toast.success('Leave type created successfully', { id: 'create-leave-type' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] });
    },
  });
}

/**
 * Update a leave type
 */
export function useUpdateLeaveType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & UpdateLeaveTypeInput) =>
      updateLeaveType(id, data),

    onMutate: () => {
      toast.loading('Updating leave type...', { id: 'update-leave-type' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'update-leave-type' });
    },

    onSuccess: () => {
      toast.success('Leave type updated successfully', { id: 'update-leave-type' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] });
    },
  });
}

/**
 * Remove an unused leave type, or deactivate it when audit history exists.
 */
export function useDeleteLeaveType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteLeaveType,

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError));
    },

    onSuccess: () => {
      toast.success('Leave type archived or removed');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] });
    },
  });
}

/**
 * Initialize default leave types for the tenant (SAP SCC4 — idempotent).
 * Used by the LeaveTypesPage empty state for quick setup.
 */
export function useInitializeLeaveTypes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: initializeLeaveTypes,

    onMutate: () => {
      toast.loading('Creating default leave types...', { id: 'init-leave-types' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'init-leave-types' });
    },

    onSuccess: (data) => {
      toast.success(
        data.created > 0
          ? `${data.created} default leave types created`
          : 'Default leave types already exist',
        { id: 'init-leave-types' },
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] });
    },
  });
}

/**
 * Initialize leave balances for all employees (admin backfill)
 */
export function useInitializeAllBalances() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ year, dryRun }: { year?: number; dryRun?: boolean }) =>
      initializeAllBalances(year, dryRun),
    onSuccess: (result) => {
      toast.success(
        result.dryRun
          ? 'Balance initialization validation recorded'
          : 'Balance initialization started. This may take a few minutes.',
      );
      // Invalidate all leave queries to refresh balances when viewed
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
      queryClient.invalidateQueries({ queryKey: ['leaveAdminOperationRuns'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to initialize balances');
    },
  });
}

// =============================================================================
// ATTACHMENT HOOKS
// =============================================================================

/**
 * Fetch attachments for a leave request
 */
export function useLeaveAttachments(requestId: string | null) {
  return useQuery({
    queryKey: [...queryKeys.leaves.detail(requestId ?? ''), 'attachments'],
    queryFn: () => fetchLeaveAttachments(requireQueryString(requestId, 'Leave request ID')),
    enabled: !!requestId,
  });
}

/**
 * Upload an attachment to a leave request
 */
export function useUploadLeaveAttachment(requestId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadLeaveAttachment(requestId, file),

    onMutate: () => {
      toast.loading('Uploading attachment...', { id: 'upload-attachment' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'upload-attachment' });
    },

    onSuccess: () => {
      toast.success('Attachment uploaded', { id: 'upload-attachment' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.leaves.detail(requestId), 'attachments'],
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

/**
 * Delete an attachment from a leave request
 */
export function useDeleteLeaveAttachment(requestId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId: string) => deleteLeaveAttachment(requestId, attachmentId),

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError));
    },

    onSuccess: () => {
      toast.success('Attachment removed');
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.leaves.detail(requestId), 'attachments'],
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

// =============================================================================
// APPROVAL STATUS HOOKS
// =============================================================================

/**
 * Fetch multi-level approval status for a leave request
 */
export function useApprovalStatus(requestId: string | null) {
  return useQuery({
    queryKey: [...queryKeys.leaves.detail(requestId ?? ''), 'approval-status'],
    queryFn: () => fetchApprovalStatus(requireQueryString(requestId, 'Leave request ID')),
    enabled: !!requestId,
  });
}

// =============================================================================
// TRANSACTION HOOKS
// =============================================================================

/**
 * Fetch leave transactions with filters
 */
export function useLeaveTransactions(filters?: LeaveTransactionFilters) {
  return useQuery({
    queryKey: ['leaveTransactions', filters],
    queryFn: () => fetchLeaveTransactions(filters),
    enabled: !!filters?.employeeId,
  });
}

// =============================================================================
// ENCASHMENT HOOKS
// =============================================================================

/**
 * Fetch encashment requests
 */
export function useEncashments(params?: { status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['leaveEncashments', params],
    queryFn: () => fetchEncashments(params),
  });
}

/**
 * Create an encashment request
 */
export function useCreateEncashment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEncashment,

    onMutate: () => {
      toast.loading('Requesting encashment...', { id: 'create-encashment' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'create-encashment' });
    },

    onSuccess: () => {
      toast.success('Encashment request created', { id: 'create-encashment' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveEncashments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

/**
 * Approve an encashment request
 */
export function useApproveEncashment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: approveEncashment,

    onMutate: () => {
      toast.loading('Approving encashment...', { id: 'approve-encashment' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'approve-encashment' });
    },

    onSuccess: () => {
      toast.success('Encashment approved', { id: 'approve-encashment' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveEncashments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

/**
 * Reject an encashment request
 */
export function useRejectEncashment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectEncashment(id, reason),

    onMutate: () => {
      toast.loading('Rejecting encashment...', { id: 'reject-encashment' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'reject-encashment' });
    },

    onSuccess: () => {
      toast.success('Encashment rejected', { id: 'reject-encashment' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveEncashments'] });
    },
  });
}

// =============================================================================
// BALANCE ADJUSTMENT HOOKS
// =============================================================================

/**
 * Adjust an employee's leave balance (admin)
 */
export function useAdjustLeaveBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adjustLeaveBalance,

    onMutate: () => {
      toast.loading('Adjusting balance...', { id: 'adjust-balance' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'adjust-balance' });
    },

    onSuccess: () => {
      toast.success('Balance adjusted successfully', { id: 'adjust-balance' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
}

// =============================================================================
// DELEGATION HOOKS (SAP HRBAS00 Substitution)
// =============================================================================

// Re-export from shared hook — used by both Leave and Attendance features.
// Keeps the same public API for existing consumers.
export { useDelegations } from '@/hooks/useDelegations';

/**
 * Create an approval delegation
 */
export function useCreateDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDelegation,

    onMutate: () => {
      toast.loading('Creating delegation...', { id: 'create-delegation' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'create-delegation' });
    },

    onSuccess: () => {
      toast.success('Delegation created', { id: 'create-delegation' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: delegationKeys.all });
    },
  });
}

/**
 * Cancel an active delegation
 */
export function useCancelDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelDelegation,

    onMutate: () => {
      toast.loading('Cancelling delegation...', { id: 'cancel-delegation' });
    },

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError), { id: 'cancel-delegation' });
    },

    onSuccess: () => {
      toast.success('Delegation cancelled', { id: 'cancel-delegation' });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: delegationKeys.all });
    },
  });
}

// =============================================================================
// MANAGED EMPLOYEES HOOK (SAP P_ORGIN SCOPE)
// =============================================================================

/**
 * Fetch employees the current user can manage (for on-behalf operations).
 * Lazily fetched — only fires when `enabled` is true.
 */
export function useManagedEmployees(enabled = false) {
  return useQuery({
    queryKey: ['hierarchy', 'managed-employees'],
    queryFn: fetchManagedEmployees,
    enabled,
  });
}

// =============================================================================
// ADMIN TRIGGER HOOKS (SAP PT_BAL00)
// =============================================================================

export function useTriggerMonthlyAccrual() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ year, month, dryRun }: { year: number; month: number; dryRun?: boolean }) =>
      triggerMonthlyAccrual(year, month, dryRun),
    onSuccess: (result) => {
      toast.success(
        result.dryRun ? 'Monthly accrual validation recorded' : 'Monthly accrual job queued',
      );
      queryClient.invalidateQueries({ queryKey: ['leaveAdminOperationRuns'] });
    },
    onError: (error) => toast.error(getErrorMessage(parseApiError(error))),
  });
}

export function useTriggerQuarterlyAccrual() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ year, month, dryRun }: { year: number; month: number; dryRun?: boolean }) =>
      triggerQuarterlyAccrual(year, month, dryRun),
    onSuccess: (result) => {
      toast.success(
        result.dryRun ? 'Quarterly accrual validation recorded' : 'Quarterly accrual job queued',
      );
      queryClient.invalidateQueries({ queryKey: ['leaveAdminOperationRuns'] });
    },
    onError: (error) => toast.error(getErrorMessage(parseApiError(error))),
  });
}

export function useTriggerYearEndRollover() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ year, dryRun }: { year: number; dryRun?: boolean }) =>
      triggerYearEndRollover(year, dryRun),
    onSuccess: (result) => {
      toast.success(
        result.dryRun ? 'Year-end rollover validation recorded' : 'Year-end rollover job queued',
      );
      queryClient.invalidateQueries({ queryKey: ['leaveAdminOperationRuns'] });
    },
    onError: (error) => toast.error(getErrorMessage(parseApiError(error))),
  });
}

export function useTriggerExpiryCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dryRun }: { dryRun?: boolean } = {}) => triggerExpiryCheck(dryRun),
    onSuccess: (result) => {
      toast.success(
        result.dryRun ? 'Carry-forward expiry validation recorded' : 'Expiry check job queued',
      );
      queryClient.invalidateQueries({ queryKey: ['leaveAdminOperationRuns'] });
    },
    onError: (error) => toast.error(getErrorMessage(parseApiError(error))),
  });
}

// =============================================================================
// PENDING APPROVAL COUNT HOOK
// =============================================================================

/**
 * Fetch count of pending approvals for the sidebar badge.
 * Uses the list endpoint with limit=1 to get the total count only.
 */
export function usePendingApprovalCount(options?: { enabled?: boolean; teamView?: TeamView }) {
  const enabled = options?.enabled ?? true;
  const teamView = options?.teamView ?? 'my-team';
  const filters = { status: 'submitted' as const, teamView, limit: 1 };

  return useQuery({
    queryKey: queryKeys.leaves.list(filters),
    queryFn: () => fetchLeaveRequests(filters),
    enabled,
    select: (data) => data?.total ?? 0,
    staleTime: 60_000,
    refetchInterval: enabled ? 60_000 : false,
  });
}

// =============================================================================
// HELPER HOOKS
// =============================================================================

/**
 * Get leave type options for select inputs
 */
export function useLeaveTypeOptions(): { value: string; label: string }[] {
  const { data: leaveTypes } = useLeaveTypes();

  if (!leaveTypes) return [];

  return leaveTypes
    .filter((lt) => lt.isActive)
    .map((lt) => ({
      value: lt.id,
      label: lt.leaveTypeName,
    }));
}

/**
 * Get leave type options filtered to encashable types only (SAP PT50 pattern).
 * Only leave types with isEncashable=true should appear in encashment forms.
 */
export function useEncashableLeaveTypeOptions(): { value: string; label: string }[] {
  const { data: leaveTypes } = useLeaveTypes();

  if (!leaveTypes) return [];

  return leaveTypes
    .filter((lt) => lt.isActive && lt.isEncashable)
    .map((lt) => ({
      value: lt.id,
      label: lt.leaveTypeName,
    }));
}

/**
 * Get available balance for a specific leave type.
 * Prefers the pre-calculated `available` from the API, with a defensive fallback.
 */
export function useAvailableBalance(employeeId: string | null, leaveTypeId: string | null): number {
  const { data: balances } = useLeaveBalance(employeeId);

  if (!balances || !leaveTypeId) return 0;

  const balance = balances.find((b) => b.leaveTypeId === leaveTypeId);
  if (!balance) return 0;

  if (balance.available != null) return balance.available;

  // Fallback: entitled + carriedForward - used - pending
  return (
    (balance.openingBalance ?? 0) +
    (balance.carriedForward ?? 0) -
    (balance.used ?? 0) -
    (balance.pending ?? 0)
  );
}
